import {MaybeReading, TemperatureAPI} from "./temperature";
import {API} from "./base";
import {OrderDirection} from "../components/generic/table";
import {GenericResponse} from "./generic";
import {SensorSet} from "./set";

export type SensorFilter = {
    ids?: number[],
    names?: string[],
    devices?: string[],
    sets?: number[],
    active?: boolean,
};

export type SensorOrder = {
    attribute: "id" | "name" | "device" | "active" | "last.date" | "last.value" | "dayMax" | "dayMin" | "highest" | "lowest" | "samples";
    direction: OrderDirection
};

export type SensorAttributes =
    "id"
    | "name"
    | "device"
    | "url"
    | "last"
    | "dayMax"
    | "dayMin"
    | "samples"
    | "highest"
    | "lowest"
    | "sets"
    | "active"
    | "since";

export type SensorsRequest = {
    filter?: SensorFilter;
    order?: SensorOrder[];
    attributes?: SensorAttributes[];
    offset?: number;
    limit?: number;
};

export type SensorUpdateRequest = {
    id: number;
    name?: string;
};

export type Sensor = {
    id: number;
    name?: string;
    url?: string;
    active?: boolean;
    last?: MaybeReading;
    dayMax?: MaybeReading;
    dayMin?: MaybeReading;
    sets?: SensorSet[];
    device?: string;
    since?: number;
    highest?: MaybeReading;
    lowest?: MaybeReading;
};

export type SensorExtended = {
    since: string;
    samples: number;
    highest: MaybeReading;
    lowest: MaybeReading;
} & Sensor;

export type SensorsResponse = GenericResponse<{
    sensors: Sensor[];
}>;

export class SensorAPI extends API {
    constructor(api: TemperatureAPI) {
        super(api.clientId, api.refreshTokenUrl, "/api/v3/sensor/")
    }

    async get(request: SensorsRequest = {}): Promise<SensorsResponse> {
        const url = new URL("", this.base);

        if (!request.filter) {
            request.filter = {
                active: true
            };
        }

        for (const [param, filter] of Object.entries({
            "id": request.filter.ids,
            "name": request.filter.names,
            "device": request.filter.devices,
            "set": request.filter.sets,
            "order": request.order ? request.order.map(o => `${o.attribute}:${o.direction}`) : undefined,
            "attribute": request.attributes,
        })) {
            if (filter !== undefined) {
                for (const val of filter) {
                    url.searchParams.append(param, val.toString());
                }
            }
        }

        for (const [param, val] of Object.entries({
            "offset": request.offset,
            "limit": request.limit,
            "active": request.filter.active
        })) {
            if (val !== undefined) {
                url.searchParams.set(param, val.toString());
            }
        }

        return (await this.fetchJSON<SensorsResponse>(url));
    }

    async update(req: SensorUpdateRequest): Promise<GenericResponse> {
        return await this.postJSON(`${req.id}`, {
            name: req.name ?? null
        }, "PATCH");
    }

    async trim(sensor: Sensor, from: Date, to: Date): Promise<GenericResponse> {
        const url = new URL(`../admin/sensor/${sensor.id}/trim`, this.base);
        url.searchParams.set("outside", "true")
        url.searchParams.set("start", from.toISOString());
        url.searchParams.set("end", to.toISOString());

        return await this.fetchJSON(url, {method: "DELETE"});
    }

    async cap(sensor: Sensor, min: number, max: number): Promise<GenericResponse> {
        const url = new URL(`../admin/sensor/${sensor.id}/cap`, this.base);
        url.searchParams.set("outside", "true");
        url.searchParams.set("min", min.toString());
        url.searchParams.set("min", max.toString());

        return await this.fetchJSON(url, {method: "DELETE"});
    }

    async merge(sensor: Sensor, targetSensorId: number): Promise<GenericResponse> {
        const url = new URL(`../admin/sensor/merge/${sensor.id}/${targetSensorId}`, this.base);
        return await this.fetchJSON(url, {method: "POST"});
    }
}
